import { Stack, styled, Typography, Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Button } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AutoResizeTextarea } from 'components/AutoResizeTextarea';
import {
  usePostApiV5FilesPreferredFormat,
  getGetApiV5FilesPreferredFormatQueryKey,
} from 'api/v5/files/files';
import { AlertMessage } from 'utilities/utils';
import { useQueryClient } from '@tanstack/react-query';
import { addParameterValidation } from './utils/usedParameterValidator';

const Wrapper = styled(Stack)({
  padding: '16px',
  border: `solid 1px #00afcc`,
  borderRadius: '4px',
});

const Info = styled(Box)({
  borderRadius: '4px',
  padding: '10px',
  marginBottom: '10px',
  fontSize: '0.95em',
  lineHeight: 'normal',
  backgroundColor: '#F5F3B4',
});

interface FormValues {
  namingFormat: string;
}

interface AINamingFormatSettingsFormProps {
  preferredFormat: string;
}

const schema = yup.object().shape({
  namingFormat: addParameterValidation(
    yup
      .string()
      .required('Naming format is required')
      .min(3, 'Format must be at least 3 characters'),
  ),
});

export const AINamingFormatSettingsForm = ({
  preferredFormat,
}: AINamingFormatSettingsFormProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      namingFormat: preferredFormat,
    },
  });

  // Watch for changes to namingFormat field to ensure proper resizing
  const namingFormatValue: string = watch('namingFormat');

  const {
    mutateAsync: savePreferredFormat,
    isLoading: isSavingPreferredFormat,
  } = usePostApiV5FilesPreferredFormat();

  const onFormSubmit = async (data: FormValues) => {
    try {
      await savePreferredFormat({ data: { format: data.namingFormat } });

      // Invalidate the preferred format query to ensure fresh data
      queryClient.invalidateQueries(getGetApiV5FilesPreferredFormatQueryKey());

      // Invalidate any naming suggestions queries to ensure they use the new format
      // This will affect all file naming suggestions across the app
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey[0]?.includes('/naming_suggestions');
        },
        refetchType: 'all', // Force refetch for active and inactive queries
      });

      AlertMessage('success', 'Naming format updated successfully', 3000);
    } catch (error) {
      AlertMessage('error', 'Failed to update naming format', 3000);
    }
  };

  return (
    <Wrapper mt={2} gap={2}>
      <Info>Organization-wide file naming format</Info>
      <div
        className={
          errors.namingFormat
            ? 'd-block form-group form-error mb-0'
            : 'd-block form-group mb-0'
        }
        style={{ flex: 1 }}
      >
        <AutoResizeTextarea
          {...register('namingFormat')}
          className="form-control"
          autoComplete="off"
          placeholder="Enter naming format pattern"
          disabled={isSavingPreferredFormat}
          // Pass the watched value to ensure the textarea resizes when changed externally
          value={namingFormatValue}
          onKeyDown={(e) => {
            // We can't use a normal form element here because this component is rendered inside another form
            // Instead, we manually trigger the submit handler when Enter is pressed
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit(onFormSubmit)(e);
            }
          }}
        />
      </div>

      <span className={errors.namingFormat ? 'error_label' : 'd-none'}>
        {errors.namingFormat?.message}
      </span>
      <Button
        className="btn btn-outline-light"
        size="sm"
        onClick={handleSubmit(onFormSubmit)}
        disabled={isSavingPreferredFormat}
      >
        Save
      </Button>

      <Typography variant="body2" color="#919eab">
        {`Use variables: {account}, {type}, {date}, {contact}, {year}, {month}, {category}, {issuer}`}
      </Typography>
    </Wrapper>
  );
};
